import React from "react";
import { Box, Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel, Chip } from "@material-ui/core";
import { css } from "@emotion/css";
import { LOCALES } from "../api";
import type { PreviewAttrs, PreviewLocale } from "../api";
import { useLocaleDiffPreviews, formatLocaleLabel } from "../useLocaleDiffPreviews";
import type { SnapshotRef } from "../useLocaleDiffPreviews";
import QuestionnaireDiff from "./QuestionnaireDiff";

const DiffShell: React.VFC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  before: SnapshotRef | null;
  after: SnapshotRef | null;
  draftId: number | null;
  attrs: PreviewAttrs;
  onAttrsChange: React.Dispatch<React.SetStateAction<PreviewAttrs>>;
  previousLabel: string;
  nextLabel: string;
  emptyMessage: React.ReactNode;
  actions?: React.ReactNode;
  isLoading?: boolean;
  error?: React.ReactNode;
}> = ({
  title,
  subtitle,
  before,
  after,
  draftId,
  attrs,
  onAttrsChange,
  previousLabel,
  nextLabel,
  emptyMessage,
  actions,
  isLoading,
  error,
}) => {
  const { localeInfo, current, previewsLoading, anyError } = useLocaleDiffPreviews({
    before,
    after,
    draftId,
    attrs,
  });

  const loading = isLoading || previewsLoading;
  const showError = !loading && (error || anyError);
  const showEmpty = !loading && !showError && (!before || !after);
  const showDiff = !loading && !showError && !showEmpty && current.before && current.after;

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box>
          <Box className={styles.title}>
            <Typography variant="h5">{title}</Typography>
            {showDiff && current.count !== null && (
              <Chip
                size="small"
                label={`${current.count} change${current.count === 1 ? "" : "s"}`}
                color={current.count === 0 ? "default" : "primary"}
              />
            )}
          </Box>
          {subtitle}
        </Box>
        <Box className={styles.actions}>
          {actions}
          <FormControl size="small" variant="outlined" className={styles.localeSelect}>
            <InputLabel>Locale</InputLabel>
            <Select
              value={attrs.locale}
              label="Locale"
              onChange={(e) => onAttrsChange((a) => ({ ...a, locale: e.target.value as PreviewLocale }))}
              renderValue={(v) =>
                formatLocaleLabel(v as PreviewLocale, localeInfo.find((l) => l.locale === v))
              }
            >
              {LOCALES.map((l) => (
                <MenuItem key={l} value={l}>
                  {formatLocaleLabel(l, localeInfo.find((i) => i.locale === l))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box className={styles.body}>
        {loading ? (
          <Box className={styles.centered}>
            <CircularProgress />
          </Box>
        ) : showError ? (
          <Typography color="error" className={styles.message}>
            {error ?? "Failed to load preview data"}
          </Typography>
        ) : showEmpty ? (
          <Typography variant="body2" color="textSecondary" className={styles.message}>
            {emptyMessage}
          </Typography>
        ) : showDiff ? (
          <QuestionnaireDiff
            previous={current.before!.questionnaire}
            next={current.after!.questionnaire}
            previousLabel={previousLabel}
            nextLabel={nextLabel}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default DiffShell;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 90px);
    padding: 16px;
    box-sizing: border-box;
    gap: 16px;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  `,
  title: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
  `,
  localeSelect: css`
    min-width: 160px;
  `,
  body: css`
    flex: 1;
    overflow: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
  `,
  centered: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  `,
  message: css`
    padding: 16px;
  `,
};
